// Поездки пользователя. Одна поездка на сервере — один workspace: своя
// библиотека блоков, свои варианты и своя публичная ссылка.
//
// Поездка бывает своей и общей: владелец зовёт соавторов по адресу почты, и
// дальше они правят одно и то же состояние. Роль приходит вместе со строкой
// списка — по ней интерфейс решает, что человеку показывать.
import { request } from '../auth/request';
import type { ApiResult } from '../auth/request';
import type { AppState } from '../types';

/** 'owner' — всё, включая приглашения и удаление; 'editor' — правка содержимого. */
export type TripRole = 'owner' | 'editor';

export interface TripMeta {
  id: string;
  /** Пустое имя — обычное дело: список подставляет вместо него подпись по умолчанию. */
  name: string;
  rev: number;
  updatedAt: number;
  createdAt: number;
  /** Размер состояния в байтах — по нему видно, пустая поездка или нет. */
  size: number;
  /** id публичной ссылки, если поездка опубликована. */
  shareId: string | null;
  /** Роль смотрящего в этой поездке. */
  role: TripRole;
  /** Сколько соавторов позвано помимо владельца. */
  members: number;
  ownerName: string;
  ownerEmail: string;
}

export interface TripMember {
  userId: string;
  email: string;
  name: string;
  role: TripRole;
  /** Когда человека позвали (у владельца — когда завели поездку). */
  createdAt: number;
}

export interface TripAccess {
  owner: TripMember;
  members: TripMember[];
  /** Роль того, кто спрашивает: владельцу доступны приглашения. */
  role: TripRole;
}

export function listTrips(): Promise<ApiResult<{ trips: TripMeta[] }>> {
  return request('/api/trips');
}

/** Начальное состояние присылает клиент: модель данных живёт здесь, не на сервере. */
export function createTrip(name: string, state: AppState): Promise<ApiResult<{ trip: TripMeta }>> {
  return request('/api/trips', { method: 'POST', json: { name, state } });
}

export function renameTrip(id: string, name: string): Promise<ApiResult<{ trip: TripMeta }>> {
  return request(`/api/trips/${encodeURIComponent(id)}`, { method: 'PATCH', json: { name } });
}

export function deleteTrip(id: string): Promise<ApiResult<void>> {
  return request(`/api/trips/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function listMembers(id: string): Promise<ApiResult<TripAccess>> {
  return request(`/api/trips/${encodeURIComponent(id)}/members`);
}

/**
 * Приглашение по адресу почты. Аккаунт у приглашённого должен уже быть —
 * иначе сервер отвечает 'no_user', и интерфейс просит позвать человека
 * зарегистрироваться.
 *
 * `lang` — язык письма-уведомления. Своего языка приглашённого сервер не
 * знает, поэтому берётся язык интерфейса того, кто зовёт.
 */
export function addMember(
  id: string,
  email: string,
  lang: string,
): Promise<ApiResult<{ member?: TripMember; pending?: boolean; email?: string }>> {
  // pending: аккаунта с такой почтой ещё нет — приглашение отложено, человеку
  // ушло письмо, доступ появится сам после регистрации этого адреса.
  return request(`/api/trips/${encodeURIComponent(id)}/members`, {
    method: 'POST',
    json: { email, lang },
  });
}

/** Владельцем — снять доступ, соавтором со своим id — выйти из поездки. */
export function removeMember(id: string, userId: string): Promise<ApiResult<void>> {
  return request(
    `/api/trips/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
}
